import idx from 'idx';
import invariant from 'tiny-invariant';
import Config from 'Config';
import { once } from 'lodash';
import WebSocket from 'isomorphic-ws';
import { emitter } from '../emitter';
import { ApolloClient, ApolloLink, Operation, split, createHttpLink } from '@apollo/client';
import { getMainDefinition } from 'apollo-utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { newInMemoryCache } from 'lib/apollo/cache/memory';
import { getCommonHeaders, getCsrfTokenFromCookies } from 'lib/requestHeaders';
import {
  ApplicationContextLink,
  NetworkErrorLink,
  QueryErrorLink,
  LogQueryLink,
  RetryLink,
} from 'lib/apollo/links';

/* The application context link is responsible for
 * adding any kind of stuff into the request context.
 */
const applicationContextLink = new ApplicationContextLink({
  // @ts-expect-error ts-migrate(2322) FIXME: Type '() => { 'Remind101-Client-Type': string; 'Re... Remove this comment to see the full error message
  getHeaders: () => getCommonHeaders(getCsrfTokenFromCookies()),
  getTimeout: () => 5000,
});

/* The network error link is responsible for surfacing any
 * network errors between the browser and the apollo server
 *
 * See lib/apolloErrorListener.js
 */
const networkErrorLink = new NetworkErrorLink({
  status: {
    '*': (code: string) => {
      emitter.emit(`network_error.${code}`);
    },
  },
});

/* The query error link is responsible for surfacing any
 * HTTP errors that occured during the execution of the query on the server.
 * For example, If the graphql server uses a downstream services that returns a 503,
 * and the graphql server does not catch/handle that error, the errors will surface here.
 *
 * See lib/apolloErrorListener.js
 */
const queryErrorLink = new QueryErrorLink({
  status: {
    '*': (codes: string[]) => {
      for (const code of codes) {
        emitter.emit(`query_error.${code}`);
      }
    },
  },
});

/* The retry link is responsible for retrying any
 * operations allowed by the shouldRetry predicate
 * function that failed because of a query or network error.
 *
 * See lib/apollo/links/RetryLink.js
 */
const retryLink = new RetryLink({
  shouldRetry: (operation: Operation, errors: any[]) => {
    const { operationName } = operation;
    if (operationName === 'ContentSourcesContent') {
      return !!errors.find((e) => idx(e, (_) => _.serviceMetadata.service_status) === 503);
    }
    if (operationName === 'UpdateUserPIIConsent' && !!errors.find((e) => e.statusCode === 429)) {
      return true;
    }
    return false;
  },
});

const handleSpecialStorybookSwitchToHttps = (apiUrl: string) => {
  if (apiUrl.substring(0, 5) === 'http:') {
    apiUrl = apiUrl.replace('http:', 'https:');
  }
  return apiUrl;
};

// TODO unify with server's client
/* The HTTP link is the terminating link of the chain.
 * It must always to placed last as it does not forward the
 * operation, but transforms the operation into an observable result.
 *
 * This is inside getApolloClientSingleton() because this browser.js file
 * is imported even in server side rendering ... and it crashed
 * because there's no global.fetch.
 */
export const getApolloClient: (addHttpsIfNeeded?: boolean) => ApolloClient<any> = (
  addHttpsIfNeeded = false
) => {
  let hqUrl = Config.Environment.R101_HQ_URL;
  invariant(hqUrl, 'Missing R101_HQ_URL');

  let hqWsUrl = Config.Environment.R101_HQ_URL_SUBSCRIPTIONS;
  invariant(hqWsUrl, 'Missing R101_HQ_URL_SUBSCRIPTIONS');

  let apiUrl = Config.Environment.R101_API_URL;
  invariant(apiUrl, 'Missing R101_API_URL');

  const apiWsURI = Config.Environment.R101_SUBSCRIPTIONS_URL;
  invariant(apiWsURI, 'Missing R101_SUBSCRIPTIONS_URL');

  /**
   * When we deploy to stage/prod, storybook is put under /internal/components and under https
   * this is a switch on the testing url so that it uses https instead of http
   * otherwise the msw calls would get blocked
   */
  if (addHttpsIfNeeded) {
    apiUrl = handleSpecialStorybookSwitchToHttps(apiUrl!);
  }

  const apiHttpLink = createHttpLink({
    uri: `${apiUrl}/graphql`,
  });

  // ws is not set in server because subscriptions do not happen there
  const apiWsLink = new GraphQLWsLink(
    createClient({
      url: apiWsURI,
      lazy: true,
      connectionParams: {},
      // This is needed because although this file is called browser it also is called in the backend due to our
      // poor ssr structure
      webSocketImpl: WebSocket,
    })
  );

  /**
   * This represents the r101-apollo server connection for
   * both queries and mutations through http and
   * subscriptions through websockets.
   */
  const apiLink = split(
    ({ query }) => {
      const def = getMainDefinition(query);
      return def.kind === 'OperationDefinition' && def.operation === 'subscription';
    },
    apiWsLink,
    apiHttpLink
  );

  const hqHttpLink = createHttpLink({
    uri: hqUrl,
  });

  // ws is not set in server because subscriptions do not happen there
  const hqWsLink = new GraphQLWsLink(
    createClient({
      url: hqWsUrl,
      lazy: true,
      connectionParams: {},
      keepAlive: 10_000,
      shouldRetry: () => true,
      retryAttempts: 10,
      // This is needed because although this file is called browser it also is called in the backend due to our
      // poor ssr structure
      webSocketImpl: WebSocket,
    })
  );

  /**
   * This represents the r101-hq server connection for
   * both queries and mutations through http and
   * subscriptions through websockets.
   */
  const hqLink = split(
    ({ query }) => {
      const def = getMainDefinition(query);
      return def.kind === 'OperationDefinition' && def.operation === 'subscription';
    },
    hqWsLink,
    hqHttpLink
  );

  /**
   * This represents all the available graphql servers: r101-apollo and r101-hq
   * The former is the default and you can optionally choose to ping r101-hq by
   * using `context: { hq: true }` in your graphql query.
   */
  const link = split((operation) => operation.getContext().hq, hqLink, apiLink);

  const cache = newInMemoryCache();

  // restore the cache from the possible server side rendering.
  if (typeof window !== 'undefined') {
    const data = idx(window as any, (_) => _.initialAppData.apolloData);

    if (data) {
      cache.restore(data);
    }
  }

  return new ApolloClient({
    cache,
    link: ApolloLink.from([
      new LogQueryLink(),
      applicationContextLink,
      queryErrorLink,
      networkErrorLink,
      retryLink,
      link,
    ]),
  });
};

// separate apollo client for long running queries (e.g. admin overview/stats dashboard)
// the /graphql_long endpoint gets rewritten to /graphql in r101-router, but just
// uses a different timeout configuration because the default 30s timeout is too short
// for some of our admin stats messaging queries (especially for large districts)
export const getApolloLongClient: (addHttpsIfNeeded?: boolean) => ApolloClient<any> = (
  addHttpsIfNeeded = false
) => {
  let apiUrl = Config.Environment.R101_API_URL;
  invariant(apiUrl, 'Missing R101_API_URL');

  if (addHttpsIfNeeded) {
    apiUrl = handleSpecialStorybookSwitchToHttps(apiUrl!);
  }

  // Specifically using graphql_long endpoint for extended timeout operations
  const longRunningHttpLink = createHttpLink({
    uri: `${apiUrl}/graphql_long`,
  });

  const cache = newInMemoryCache();

  // Restore cache from SSR if available
  if (typeof window !== 'undefined') {
    const data = idx(window as any, (_) => _.initialAppData.apolloData);
    if (data) {
      cache.restore(data);
    }
  }

  return new ApolloClient({
    cache,
    // Only including necessary links for HTTP operations
    link: ApolloLink.from([
      new LogQueryLink(),
      applicationContextLink,
      queryErrorLink,
      networkErrorLink,
      retryLink,
      longRunningHttpLink,
    ]),
  });
};

export const getApolloClientSingleton: (addHttpsIfNeeded?: boolean) => ApolloClient<any> =
  once(getApolloClient);

export const getApolloLongClientSingleton: (addHttpsIfNeeded?: boolean) => ApolloClient<any> =
  once(getApolloLongClient);
